import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useBalance } from 'src/hooks/useBalance';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import SkeletonImage from '@com/utils/SkeletonImage';
import loadable from '@loadable/component';
import styled from '@emotion/styled';

const Footer = loadable(() => import('@com/footer/Footer'));
const FullSectionText = loadable(() => import('@com/FullSectionText'));
const Header = loadable(() => import('@com/header/Header'));
const LoadingContainer = loadable(() => import('@com/utils/LoadingContainer'));
const TopSection = loadable(() => import('@com/TopSection'));

const TableContainer = styled.div`
  overflow-x: scroll !important;
`;

const Table = styled.table`
  color: var(--primary-color2);
  font-size: 2rem;
  min-width: 600px;
`;

const TableHeader = styled.th`
  width: 25%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle !important;
`;

const TableData = styled.td`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScrollIcon = styled.i`
  width: 68px !important;
  height: 68px !important;
  line-height: 68px !important;
  margin-right: 15px !important;
`;

const LogoContainer = styled.div`
  width: min(300px, 100%);
`;

function BalanceDetail(): JSX.Element {
  /* Define state / hook */
  const { invoiceId } = useParams();
  const { t, ready } = useTranslation();
  const navigate = useNavigate();
  const { getBalanceDetail } = useAPI();
  const { getBadge, getAmount, getInvoiceDate } = useBalance();

  const { data, isSuccess, isError } = useQuery(
    [getBalanceDetail.name, invoiceId],
    () => {
      return invoiceId ? getBalanceDetail(invoiceId) : null;
    }
  );

  /* Custom Data Object for render or other usages */
  const nft = data?.nft;
  const collection = data?.collection;
  const collectionIdentifier = collection?.custom_url || collection?.address;
  const displayImage = nft?.preview || collection?.logo_image;
  const chainId = collection?.network_chain_id;
  const url = nft
    ? `/nft/${chainId}/${collectionIdentifier}/${nft?.token_id}`
    : `/collection/${chainId}/${collectionIdentifier}`;

  /* Render control function / logic */

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    if (!invoiceId && ready) {
      toast.error(t('receipt.noInvoiceNum'));
      navigate('/balance');
    }
  }, [ready]);

  /* Styling */

  /* Render */
  return (
    <>
      <Header />
      <TopSection sectionName={t('bankReceipt.amountStatement')} />
      <FullSectionText>
        <LoadingContainer isError={isError} isSuccess={isSuccess}>
          <>
            <div className="mb-5">
              {data?.type && getBadge(data.type, 'lg')}
            </div>
            <div className="sc-card-activity style1 row">
              {displayImage && (
                <div className="col-xl-3 col-12 mb-5">
                  <LogoContainer>
                    <Link to={url}>
                      <SkeletonImage
                        alt="Asset Logo"
                        ratio={1}
                        size={[512]}
                        src={displayImage}
                      />
                    </Link>
                  </LogoContainer>
                </div>
              )}
              <TableContainer className="col-xl col-12">
                <div className="d-flex align-items-center mb-5">
                  <ScrollIcon className="button-active fas fa-scroll" />
                  <div className="mr-4">{data?.event}</div>
                  <Link className="ml-auto" to="/balance">
                    <i className="fas fa-lg fa-long-arrow-alt-left" />
                  </Link>
                </div>
                <Table className="table">
                  <tbody>
                    <tr>
                      <TableHeader>{t('receipt.invoiceNum')}</TableHeader>
                      <TableData>{data?.record_number || '-'}</TableData>
                    </tr>
                    <tr>
                      <TableHeader>{t('bankReceipt.category')}</TableHeader>
                      <TableData>{data?.type}</TableData>
                    </tr>
                    <tr>
                      <TableHeader>{t('bankReceipt.amount')}</TableHeader>
                      <TableData>
                        {getAmount(data?.amount, data?.currency)}
                      </TableData>
                    </tr>
                    <tr>
                      <TableHeader>{t('bankReceipt.date')}</TableHeader>
                      <TableData>
                        {getInvoiceDate(data?.created_at_timestamp)}
                      </TableData>
                    </tr>
                  </tbody>
                </Table>
              </TableContainer>
            </div>
          </>
        </LoadingContainer>
      </FullSectionText>
      <Footer />
    </>
  );
}

export default BalanceDetail;
